import React, { useEffect, useState } from "react";
import type { IntlShape } from "react-intl";
import {
  buildVariableMaps,
  canonicalToDisplay,
  displayToCanonical,
} from "./variableTokens";

// 📌 Hook pour gérer les dimensions du textarea
export const useTextareaResize = (
  initialValues: {
    width: string;
    height: string;
    ref: React.RefObject<HTMLTextAreaElement> | null;
  } = { width: "100%", height: "300px", ref: null }
) => {
  const [textareaSize, setTextareaSize] = useState(initialValues);
  // Permet d'adapter la hauteur de la div en dessous du textarea
  useEffect(() => {
    function outputsize() {
      setTextareaSize({
        width:
          `${initialValues.ref?.current?.offsetWidth}px` || initialValues.width,
        height:
          `${initialValues.ref?.current?.offsetHeight}px` ||
          initialValues.height,
        ref: initialValues.ref,
      });
    }
    outputsize();

    if (initialValues.ref?.current) {
      new MutationObserver(outputsize).observe(initialValues.ref.current, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }
  }, []);
  return { textareaSize, setTextareaSize };
};

// 📌 Hook pour gérer les mentions dans le texte
export const useMentionsAndVariables = (
  mentions: Record<string, { name: string }> | undefined,
  variables: Record<string, string | null>,
  intl: IntlShape
) => {
  // Correspondances clé canonique <-> token localisé pour les variables par défaut
  const variableMaps = buildVariableMaps(intl, variables);

  // Texte affiché -> texte stocké : @nom -> $MENTION, token localisé -> clé canonique
  const normalizeMentionsAndVariables = (text: string) => {
    let parsedInput = text;
    Object.entries(mentions || {}).forEach(([key, value]) => {
      parsedInput = parsedInput?.replaceAll(`@${value.name}`, key);
    });
    return displayToCanonical(parsedInput || "", variableMaps);
  };

  // Remplace les $MENTION et les $VARIABLE par des <span> (en affichant le token localisé)
  const generateMentionOrVariableSpans = (input: string) => {
    const parsedInput = input
      ?.replaceAll(/(\$MENTION\d+)/g, (match) => {
        const mentionName = mentions?.[match]?.name;
        return mentionName
          ? `<span class="bg-brand_focus bg-opacity-20 text-brand_darker rounded-md">@${mentionName}</span>`
          : match;
      })
      .replaceAll(/(\$\w+)/g, (match) => {
        const key = match.replace(/^\$/, "");
        const display = variableMaps.keyToToken[key]
          ? `$${variableMaps.keyToToken[key]}`
          : match;
        return variables?.[match]
          ? `<span class="underline decoration-dashed underline-offset-2">${display}</span>`
          : display;
      });
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: parsedInput,
        }}
      />
    );
  };

  // Texte stocké -> texte affiché : $MENTION1 -> @nom, clé canonique -> token localisé
  const displayMentionOrVariableNames = (input: string) => {
    const normalizedInput = input?.replace(/\$(MENTION\d+)/g, (match) => {
      return mentions?.[match]?.name ? `@${mentions[match].name}` : match;
    });
    return canonicalToDisplay(normalizedInput || "", variableMaps);
  };

  return {
    normalizeMentionsAndVariables,
    generateMentionOrVariableSpans,
    displayMentionOrVariableNames,
  };
};
